// Layers on a Calendar board (Calendar plan, step 6): what has a date on the user's other boards, shown on the
// calendar by itself, one layer per board: a content calendar's posts on their day (at their time when they have one;
// a posted one faded) and a pipeline's follow-ups (an open deal's next step, all day on its due day). Their days and
// times are the user's own, as their boards keep them, so they show on the viewer's clock as they are. Layers stay on
// Context Board: nothing here goes to Google. Each shows as an event the calendar draws, its calendar "layer:<board>",
// its source in meta.layer.

import { addLocal, shownFrom, type EventData, type Shown } from "./agenda";
import { kindOf } from "./boardKinds";
import { channelName, postLabel, postOf } from "./calendar";
import { dealName, type DealData } from "./pipeline";
import type { Board, BoardKind, Entry, ID } from "./types";

export type LayerKind = "post" | "followup";

/** Where a layer item comes from: its entry on its own board, and what it says besides its title. */
export interface LayerMeta {
  kind: LayerKind;
  entry: ID;
  board: ID;
  /** a post's channel and status; a follow-up's contact, when its title names the company */
  sub?: string;
  /** a post that went out: shown faded */
  faded?: boolean;
}

/** The kinds of board whose dated things are layers, and the kind of layer item each holds. */
export const LAYER_OF = { calendar: "post", pipeline: "followup" } as const satisfies Partial<Record<BoardKind, LayerKind>>;
export const LAYER_KINDS = Object.keys(LAYER_OF) as BoardKind[];

/** A layer's key, as a Calendar board keeps it among its hidden calendars (CalSetData.hidden). */
export const layerKey = (board: ID) => `layer:${board}`;
export const isLayer = (cal: string) => cal.startsWith("layer:");

/** The colors layers are drawn in, by kind: never a Google calendar's, and each kind its own. */
export const LAYER_COLORS: Record<LayerKind, string> = { post: "var(--tc-purple)", followup: "var(--tc-green)" };

/** The boards that are layers: content calendars and pipelines, archived ones left out. */
export function layerBoards(boards: Board[]): Board[] {
  return boards.filter((b) => LAYER_KINDS.includes(kindOf(b)) && !b.archived);
}

/**
 * The dated things on the layer boards that touch the days a to b, as events the calendar draws, on the viewer's
 * clock (tz). hidden: a Calendar board's hidden keys; a layer there is left out.
 */
export function layerShowings(boards: Board[], entries: Entry[], a: string, b: string, tz: string | null, hidden: string[] = []): Shown[] {
  const on = new Map(layerBoards(boards).filter((x) => !hidden.includes(layerKey(x.id))).map((x) => [x.id, kindOf(x)]));
  const out: Shown[] = [];
  for (const e of entries) {
    const kind = on.get(e.boardId);
    if (!kind) continue;
    const s = kind === "calendar" && e.t === "post" ? fromPost(e, tz) : kind === "pipeline" && e.t === "deal" ? fromDeal(e) : null;
    if (s && s.first <= b && s.last >= a) out.push(s);
  }
  return out;
}

const idOf = (e: Pick<Entry, "id" | "boardId">) => `${layerKey(e.boardId)}#${e.id}`;

function shown(e: Entry, ev: EventData, tz: string | null, layer: LayerMeta, kind: LayerKind): Shown {
  return { ...shownFrom(idOf(e), ev, tz, layerKey(e.boardId), LAYER_COLORS[kind]), meta: { layer } };
}

/** A post on its day, at its time for half an hour, else all day. An idea with no day is no layer item. */
function fromPost(e: Entry, tz: string | null): Shown | null {
  const p = postOf(e);
  if (!p.day) return null;
  const title = postLabel(p, 80) || "Post";
  const ev: EventData = p.time ? { title, allDay: false, start: `${p.day}T${p.time}`, end: addLocal(`${p.day}T${p.time}`, 30), tz: tz ?? null } : { title, allDay: true, start: p.day, end: p.day, tz: null };
  const sub = `${channelName(p.channel)}, ${p.status}`;
  return shown(e, ev, tz, { kind: "post", entry: e.id, board: e.boardId, sub, ...(p.status === "posted" ? { faded: true } : {}) }, "post");
}

/** An open deal's next step, all day on its due day. One won, lost, or with no due day is no layer item. */
function fromDeal(e: Entry): Shown | null {
  const d = e.data as unknown as DealData;
  if (d.status !== "open" || !d.due) return null;
  const who = dealName(d);
  const title = `${who}: ${d.next?.trim() || "Follow up"}`;
  // the title names the company (or the contact, when there is none), so the line under it names the contact
  return shown(e, { title, allDay: true, start: d.due, end: d.due, tz: null }, null, { kind: "followup", entry: e.id, board: e.boardId, ...(d.company && d.contact ? { sub: d.contact } : {}) }, "followup");
}

/**
 * A layer item's entry once it is moved on the calendar to start (and end), on the user's clock (days for all day): a
 * post to its new day, at its new time when it lands at one; a follow-up to its new due day. null when there is
 * nothing to change.
 */
export function layerMoved(kind: LayerKind, data: Record<string, unknown>, to: { allDay?: boolean; start?: string; end?: string }): Record<string, unknown> | null {
  if (!to.start) return null;
  const day = to.start.slice(0, 10);
  const time = !to.allDay && to.start.length > 10 ? to.start.slice(11, 16) : null;
  if (kind === "post") return { ...data, day, time: to.allDay ? null : (time ?? data.time ?? null) };
  return { ...data, due: day };
}
